import dayjs from 'dayjs';
import capitalize from 'lodash/capitalize';
import clone from 'lodash/clone';
import cloneDeep from 'lodash/cloneDeep';

import { LanguageSlugEnum } from '@/types/regionTypes';
import { capitalizeFirstLetter } from '@/utils/string';

import type { ContentfulAsset, Maybe, Scalars } from '@/graphql/contentful/api';
import type { OrderAppCtaType } from '@/graphql/orderApp/api';
import type {
  EyewearAttributesFragment,
  Image,
  ProductVariantStrapBasicDetailsFragment,
  ProductVariantWatchBasicDetailsFragment,
  SelectedAttribute,
  StrapAttributesFragment,
  VariantPricingInfo,
  WatchAttributesFragment,
} from '@/graphql/saleor/api';
import { getBrandName, toBrandEnum } from '@/lib/configuration/configuration';
import { BRAND } from '@/lib/const';

export interface SeoVariables {
  boutiqueName?: string;
  caseMaterial?: string;
  collectionName?: string;
  country?: string;
  countryName?: string;
  dialColor?: string;
  frameColor?: string;
  frameShape?: string;
  lensColor?: string;
  lugSize?: string;
  marketingDescription?: string;
  productName?: string;
  referenceNumber?: string;
  strapMaterial?: string;
  thumbnail?: Image;
}

type ExtractValueOptions = {
  textTransform?: 'lowerCase' | 'none';
};

const extractBasicValue = (
  attribute: SelectedAttribute | undefined | null,
  options: ExtractValueOptions = {
    textTransform: 'none',
  },
) => {
  if (!attribute) {
    return '';
  }

  const value = attribute.values[0];

  if (options.textTransform === 'lowerCase') {
    return value?.translation?.plainText?.toLowerCase() ?? '';
  }

  return value?.translation?.plainText ?? '';
};

export const extractStrapMaterialValue = (
  strapMaterial: SelectedAttribute | undefined | null,
  options: ExtractValueOptions = {
    textTransform: 'lowerCase',
  },
) => {
  return extractBasicValue(strapMaterial, options);
};

export const extractCaseMaterialValue = (
  caseMaterial: SelectedAttribute | undefined | null,
  options: ExtractValueOptions = {
    textTransform: 'lowerCase',
  },
) => {
  return extractBasicValue(caseMaterial, options);
};

const extractLugSizeValue = (lugSize: SelectedAttribute | undefined | null) => {
  if (!lugSize) {
    return '';
  }
  const value = lugSize.values[0];

  return [value?.name, lugSize.attribute?.unit].filter(Boolean).join(' ');
};

export const extractDialColorValue = (
  dialColor: SelectedAttribute | undefined | null,
  options: ExtractValueOptions = {
    textTransform: 'lowerCase',
  },
) => {
  return extractBasicValue(dialColor, options);
};

// Configuration for language-specific text transformations
const TEXT_TRANSFORM_CONFIG: Record<string, ExtractValueOptions | undefined> = {
  [LanguageSlugEnum.de]: { textTransform: 'none' }, // German: no transformation
  // Add new languages here, e.g.:
  // [LanguageSlugEnum.fr]: { textTransform: 'lowerCase' }, // French: lowerCase
};

export const extractSeoVariablesFromStrapProductVariant = (
  attributes: StrapAttributesFragment,
  language?: string,
): SeoVariables => {
  const { lugSize, marketingDescription, strapMaterial } = attributes;

  // Get text transformation options for the given language, default to undefined
  const options = language ? TEXT_TRANSFORM_CONFIG[language] : undefined;
  return {
    marketingDescription: extractBasicValue(
      marketingDescription as SelectedAttribute,
    ),
    strapMaterial: extractStrapMaterialValue(
      strapMaterial as SelectedAttribute,
      options,
    ),
    lugSize: extractLugSizeValue(lugSize as SelectedAttribute),
  };
};

export const extractSeoVariablesFromWatchProductVariant = (
  attributes: WatchAttributesFragment,
  language?: string,
): SeoVariables => {
  const { caseMaterial, dialColor, lugWidth, strapMaterial } = attributes;
  const noneTextTransform: ExtractValueOptions = {
    textTransform: 'none',
  };
  const options =
    language === LanguageSlugEnum.de ? noneTextTransform : undefined;

  return {
    strapMaterial: extractStrapMaterialValue(
      strapMaterial as SelectedAttribute,
      options,
    ),
    caseMaterial: extractCaseMaterialValue(
      caseMaterial as SelectedAttribute,
      options,
    ),
    dialColor: extractDialColorValue(dialColor as SelectedAttribute, options),
    lugSize: extractLugSizeValue(lugWidth as SelectedAttribute),
  };
};

export const extractSeoVariablesFromEyewearProductVariant = (
  attributes: EyewearAttributesFragment,
  language?: string,
): SeoVariables => {
  const { frameColor, frameShape, lensColor } = attributes;
  const noneTextTransform: ExtractValueOptions = {
    textTransform: 'none',
  };
  const lowerCaseTransform: ExtractValueOptions = {
    textTransform: 'lowerCase',
  };
  const options =
    language === LanguageSlugEnum.de ? noneTextTransform : lowerCaseTransform;

  return {
    frameColor: extractBasicValue(frameColor as SelectedAttribute, options),
    frameShape: extractBasicValue(frameShape as SelectedAttribute, options),
    lensColor: extractBasicValue(lensColor as SelectedAttribute, options),
  };
};

function replaceSeoVariables(
  text: string | null | undefined,
  collection: string | null | undefined,
  strapMaterial: string | null | undefined,
  productName: string | null | undefined,
  referenceNumber: string | null | undefined,
  caseMaterial: string | null | undefined,
  lugSize: string | null | undefined,
  dialColor: string | null | undefined,
  marketingDescription: string | null | undefined,
  boutiqueName: string | null | undefined,
  countryName: string | null | undefined,
  boutiqueListH1: string | null | undefined,
  frameColor: string | null | undefined,
  frameShape: string | null | undefined,
  lensColor: string | null | undefined,
): string | undefined {
  const textWithVariables = text
    ?.replaceAll('%%COLLECTION_NAME%%', collection ?? '%%COLLECTION_NAME%%')
    .replaceAll('%%STRAP_MATERIAL%%', strapMaterial ?? '%%STRAP_MATERIAL%%')
    .replaceAll('%%BOUTIQUE_NAME%%', boutiqueName ?? '%%BOUTIQUE_NAME%%')
    .replaceAll('%%COUNTRY%%', countryName ?? '%%COUNTRY%%')
    .replaceAll(
      '%%PRODUCT_NAME%%',
      productName
        ? productName
            .replaceAll('-', ' ')
            .split(' ')
            .map((word) => capitalize(word))
            .join(' ')
        : '%%PRODUCT_NAME%%',
    )
    .replaceAll(
      '%%REFERENCE_NUMBER%%',
      referenceNumber ?? '%%REFERENCE_NUMBER%%',
    )
    .replaceAll('%%LUG_SIZE%%', lugSize ?? '%%LUG_SIZE%%')
    .replaceAll('%%DIAL_COLOR%%', dialColor ?? '%%DIAL_COLOR%%')
    .replaceAll('%%CASE_MATERIAL%%', caseMaterial ?? '%%CASE_MATERIAL%%')
    .replaceAll(
      '%%MARKETING_DESCRIPTION%%',
      marketingDescription ?? '%%MARKETING_DESCRIPTION%%',
    )
    .replaceAll(
      '%%BOUTIQUE_LIST_H1%%',
      boutiqueListH1 ?? '%%BOUTIQUE_LIST_H1%%',
    )
    .replaceAll('%%SUNGLASSES_NAME%%', productName ?? '%%SUNGLASSES_NAME%%')
    .replaceAll('%%FRAME_COLOR%%', frameColor ?? '%%FRAME_COLOR%%')
    .replaceAll('%%FRAME_SHAPE%%', frameShape ?? '%%FRAME_SHAPE%%')
    .replaceAll('%%LENS_COLOR%%', lensColor ?? '%%LENS_COLOR%%');
  return capitalizeFirstLetter(textWithVariables ?? '');
}

export type SeoConfig = {
  seoCanonical?: Maybe<Scalars['String']['output']>;
  seoDescription?: Maybe<Scalars['String']['output']>;
  seoIndex?: Maybe<Scalars['Boolean']['output']>;
  seoThumbnail?: Maybe<ContentfulAsset> | Image;
  seoTitle?: Maybe<Scalars['String']['output']>;
};

function getSeoConfig(
  seoConfig: SeoConfig | null | undefined,
  overrideSeoConfig: SeoConfig | null | undefined,
  collection: string | null | undefined,
  strapMaterial: string | null | undefined,
  productName: string | null | undefined,
  referenceNumber: string | null | undefined,
  caseMaterial: string | null | undefined,
  lugSize: string | null | undefined,
  dialColor: string | null | undefined,
  marketingDescription: string | null | undefined,
  boutiqueName: string | null | undefined,
  countryName: string | null | undefined,
  thumbnail: Image | null | undefined,
  frameColor: string | null | undefined,
  frameShape: string | null | undefined,
  lensColor: string | null | undefined,
): SeoConfig | undefined {
  const finalSeoConfig: SeoConfig | undefined = seoConfig
    ? cloneDeep(seoConfig)
    : undefined;

  function getSeoIndexValue(
    overrideSeoConfig: SeoConfig,
    finalSeoConfig: SeoConfig,
  ) {
    const seoIndex = overrideSeoConfig.seoIndex ?? finalSeoConfig.seoIndex;
    if (typeof seoIndex !== 'boolean') {
      return true;
    }
    return seoIndex;
  }

  if (seoConfig && overrideSeoConfig && finalSeoConfig) {
    finalSeoConfig.seoCanonical =
      overrideSeoConfig.seoCanonical ?? finalSeoConfig.seoCanonical;
    finalSeoConfig.seoDescription =
      overrideSeoConfig.seoDescription ?? finalSeoConfig.seoDescription;
    finalSeoConfig.seoIndex = getSeoIndexValue(
      overrideSeoConfig,
      finalSeoConfig,
    );
    const thumbnailDefault =
      overrideSeoConfig.seoThumbnail ?? finalSeoConfig.seoThumbnail;
    finalSeoConfig.seoThumbnail = thumbnail ?? thumbnailDefault;
    finalSeoConfig.seoTitle =
      overrideSeoConfig.seoTitle ?? finalSeoConfig.seoTitle;
  }
  if (finalSeoConfig) {
    finalSeoConfig.seoTitle = replaceSeoVariables(
      finalSeoConfig.seoTitle as string,
      collection,
      strapMaterial,
      productName,
      referenceNumber,
      caseMaterial,
      lugSize,
      dialColor,
      marketingDescription,
      boutiqueName,
      countryName,
      null,
      null,
      null,
      null,
    );
    finalSeoConfig.seoDescription = replaceSeoVariables(
      finalSeoConfig.seoDescription as string,
      collection,
      strapMaterial,
      productName,
      referenceNumber,
      caseMaterial,
      lugSize,
      dialColor,
      marketingDescription,
      boutiqueName,
      countryName,
      finalSeoConfig.seoTitle,
      frameColor,
      frameShape,
      lensColor,
    );
  }

  return finalSeoConfig;
}

export class SeoConfigBuilder {
  private seoVariables: SeoVariables | null | undefined;
  private readonly seoConfig: SeoConfig;
  private seoConfigOverride: SeoConfig;

  public constructor(
    seoConfig?: SeoConfig | null | undefined,
    seoConfigOverride?: SeoConfig | null | undefined,
    seoVariables?: SeoVariables | null | undefined,
  ) {
    this.seoConfig = clone(seoConfig) ?? this.createEmptySeoConfig();
    this.seoConfigOverride =
      clone(seoConfigOverride) ?? this.createEmptySeoConfig();
    this.seoVariables = seoVariables;
  }

  public getSeoVariables(): SeoVariables | null | undefined {
    return this.seoVariables;
  }

  public setSeoVariables(seoVariables: SeoVariables) {
    this.seoVariables = seoVariables;
    return this;
  }

  public setCanonical(canonical: string) {
    this.seoConfigOverride.seoCanonical = canonical;
    return this;
  }

  public mergeSeoVariables(seoVariables: SeoVariables) {
    if (!this.seoVariables) {
      this.seoVariables = seoVariables;
    } else {
      this.seoVariables = {
        ...this.seoVariables,
        ...seoVariables,
      };
    }
    return this;
  }

  public toString(): string {
    return JSON.stringify(this.build());
  }

  private createEmptySeoConfig(): SeoConfig {
    return {
      seoCanonical: null,
      seoDescription: null,
      seoIndex: null,
      seoThumbnail: null,
      seoTitle: null,
    };
  }

  public build(): SeoConfig | null {
    try {
      return (
        getSeoConfig(
          this.seoConfig,
          this.seoConfigOverride,
          this.seoVariables?.collectionName,
          this.seoVariables?.strapMaterial,
          this.seoVariables?.productName,
          this.seoVariables?.referenceNumber,
          this.seoVariables?.caseMaterial,
          this.seoVariables?.lugSize,
          this.seoVariables?.dialColor,
          this.seoVariables?.marketingDescription,
          this.seoVariables?.boutiqueName,
          this.seoVariables?.countryName,
          this.seoVariables?.thumbnail,
          this.seoVariables?.frameColor,
          this.seoVariables?.frameShape,
          this.seoVariables?.lensColor,
        ) ?? null
      );
    } catch (e) {
      console.error('Error building SEO configuration', e);
      return null;
    }
  }

  public setNoIndex() {
    this.seoConfigOverride.seoIndex = false;
    return this;
  }
}

interface Offer {
  '@type': string;
  availability?: string;
  itemCondition: string;
  price: number | null | undefined;
  priceCurrency: string | null | undefined;
  priceValidUntil: string;
  url: string;
}

export interface ProductStructuredData {
  brand: string;
  category: string | null;
  description: string | undefined;
  images: string[] | undefined | null;
  mainEntityOfPage: string;
  mpn: string | undefined | null;
  offers: Offer[];
  productID: string | undefined | null;
  productName: string | undefined;
  sku: string | undefined | null;
  url: string;
}

export function extractProductStructuredData<
  T extends
    | ProductVariantWatchBasicDetailsFragment
    | ProductVariantStrapBasicDetailsFragment,
>(
  productVariant:
    | T & {
        pricing: VariantPricingInfo;
        quantityAvailable?: number;
      },
  productUrl: string,
  productCatalogMode: boolean,
  variantCTA?: OrderAppCtaType,
): ProductStructuredData {
  const { collections, defaultVariant, media, name, watchName } =
    productVariant.product;

  let description = '';
  if ('marketingDescription' in productVariant.product) {
    description = extractBasicValue(
      productVariant.product.marketingDescription as SelectedAttribute,
    );
  }
  if ('modelStory' in productVariant.product) {
    description = extractBasicValue(
      productVariant.product.modelStory as SelectedAttribute,
    );
  }
  if ('eyewearModelStory' in productVariant.product) {
    description = extractBasicValue(
      productVariant.product.eyewearModelStory as SelectedAttribute,
    );
  }

  const productID = defaultVariant?.sku;
  const sku = defaultVariant?.sku;
  const mpn = defaultVariant?.sku;
  const productName = extractBasicValue(watchName as SelectedAttribute) || name;

  const category = collections ? collections[0]?.name : null;
  const image =
    media?.find((image) => image.shotType === 'Soldier')?.url ??
    media?.[0]?.url ??
    '';
  const brand = getBrandName(toBrandEnum(BRAND));
  const mainEntityOfPage = productUrl;
  const url = productUrl;
  // This is the rule applied in the current website
  const currentDatePlusSevenDays = dayjs().add(7, 'day').format('YYYY-MM-DD');

  const schema = 'https://schema.org/';
  const offer: Offer = {
    '@type': 'Offer',
    price: productVariant.pricing?.price?.gross.amount ?? null,
    priceCurrency: productVariant.pricing?.price?.net.currency ?? null,
    url: productUrl,
    priceValidUntil: currentDatePlusSevenDays,
    itemCondition: `${schema}NewCondition`,
  };
  if (productCatalogMode) {
    offer.availability = schema.concat('OutOfStock');
  } else if (variantCTA) {
    switch (variantCTA) {
      case 'ORDER':
        offer.availability = schema.concat('InStock');
        break;
      case 'PRE_ORDER':
        offer.availability = schema.concat('PreOrder');
        break;
      default:
        offer.availability = schema.concat('OutOfStock');
    }
  } else {
    offer.availability = schema.concat(
      productVariant.quantityAvailable ? 'InStock' : 'OutOfStock',
    );
  }
  const offers = [offer];

  return {
    productID,
    sku,
    mpn,
    productName: productName ?? null,
    description: description ?? null,
    category: category ?? null,
    images: image ? [image] : null,
    brand: brand ?? null,
    mainEntityOfPage: mainEntityOfPage ?? null,
    url: url ?? null,
    offers,
  };
}

export const SEO_DEFAULTS = {
  EN: {
    title: 'Breitling® | Swiss Luxury Watches of Style, Purpose & Action',
    description:
      'Breitling is obsessed with quality resulting in beautifully crafted & precise watches. Discover our brand new watch collection!',
  },
};
